import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Eye, EyeOff, PanelRightClose, PanelRightOpen } from "lucide-react";

// WHY [BOL-APP-WORKSPACE-TOOLBAR-001] (human 2026-09-26, R785: "this entire top bar is wasted space",
// "where is says tasks twice", "put the private button inside the window not outside in a seperate top bar",
// "Put that info in the … section that is open not another one on top", "why did you move the [counts strip]
// to the very bottom"): every view gets ONE toolbar row at the top of its own open panel — the view title, that
// view's actions, the counts strip with Summary, Private, and (split views) the side-chat toggle. This module is
// the single shared owner of that row so every view has the same shape, spacing and density.
// The desktop global top bar is gone; the shell only provides the Private state through context, so the SAME
// Private owner (App.tsx requestPrivateMode, including its Voice presentation lock) drives this button.
// Falsifier: a view shows two titles, the counts strip renders below the work area, Private stops toggling or
// masking, or a locked Voice presentation can flip Private from this button.

export interface WorkspaceChrome {
  privateMode: boolean;
  privateModeLocked: boolean;
  setPrivateMode: (enabled: boolean) => void;
}

export const WorkspaceChromeContext = createContext<WorkspaceChrome | null>(null);

// WHY [BOL-APP-WORKSPACE-PRIVATE-001]: identical markup, labels, lock and handler to the former top-bar button
// (aria-label "Private mode", aria-pressed, disabled while Voice presents private data, On/Off text), only placed
// inside the panel. Private is a J1-protected control; its behavior must not change with its position.
export function PrivateModeToggle({ privateMode, privateModeLocked, setPrivateMode }: WorkspaceChrome) {
  return (
    <button
      className={"private-mode-toggle private-mode-toggle--toolbar " + (privateMode ? "is-active" : "")}
      type="button"
      aria-label="Private mode"
      aria-pressed={privateMode}
      disabled={privateModeLocked}
      title={privateModeLocked ? "Private mode is fixed until the current Voice request finishes." : privateMode ? "Private mode is on. Show phone numbers." : "Private mode is off. Mask phone numbers."}
      onClick={() => setPrivateMode(!privateMode)}
    >
      {privateMode ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}<span>Private</span><small>{privateMode ? "On" : "Off"}</small>
    </button>
  );
}

export interface SideChatToggle {
  collapsed: boolean;
  toggle: () => void;
}

export function WorkspaceToolbar({ title, eyebrow, titleHidden = false, actions, counts, viewSlot, sideChat, variant = "panel" }: {
  title: string;
  eyebrow?: string | undefined;
  /** Screen-reader-only title (the phone Chat and the secondary pages that carry their own heading). */
  titleHidden?: boolean;
  actions?: ReactNode;
  counts?: ReactNode;
  /** Ref for the view's own controls (Tasks lens, search), portaled in by the view that owns their state. */
  viewSlot?: ((node: HTMLDivElement | null) => void) | undefined;
  sideChat?: SideChatToggle | undefined;
  variant?: "panel" | "utility";
}) {
  const chrome = useContext(WorkspaceChromeContext);
  return (
    <header className={`bol-toolbar bol-toolbar--${variant}`} aria-label={`${title} toolbar`}>
      <div className={titleHidden ? "bol-toolbar__title sr-only" : "bol-toolbar__title"}>
        {eyebrow ? <small>{eyebrow}</small> : null}<h2>{title}</h2>
      </div>
      {viewSlot ? <div className="bol-toolbar__view" ref={viewSlot} /> : null}
      {actions ? <nav className="bol-toolbar__actions" aria-label={`${title} actions`}>{actions}</nav> : null}
      <div className="bol-toolbar__end">
        {counts ? <div className="bol-toolbar__counts">{counts}</div> : null}
        {chrome ? <PrivateModeToggle {...chrome} /> : null}
        {sideChat ? (
          <button
            className="bol-chat-toggle"
            type="button"
            aria-expanded={!sideChat.collapsed}
            aria-label={sideChat.collapsed ? "Show chat" : "Hide chat"}
            title={sideChat.collapsed ? "Show chat" : "Hide chat"}
            onClick={sideChat.toggle}
          >
            {sideChat.collapsed ? <PanelRightOpen size={17} aria-hidden="true" /> : <PanelRightClose size={17} aria-hidden="true" />}<span>Chat</span>
          </button>
        ) : null}
      </div>
    </header>
  );
}

// WHY [BOL-APP-WORKSPACE-SIDECHAT-001] (R785 "the side chat can collapse"): a per-viewer convenience, default open.
// Storage can be missing or throw (private windows, blocked site data); the chat then simply starts open.
const SIDE_CHAT_KEY = "bol.workspace.sideChatCollapsed";
export function readSideChatCollapsed(): boolean {
  try { return typeof window !== "undefined" && window.localStorage.getItem(SIDE_CHAT_KEY) === "1"; } catch { return false; }
}
export function writeSideChatCollapsed(collapsed: boolean): void {
  try { if (typeof window !== "undefined") window.localStorage.setItem(SIDE_CHAT_KEY, collapsed ? "1" : "0"); } catch { /* per-viewer convenience only */ }
}

// WHY [BOL-APP-WORKSPACE-DESKTOP-001]: view controls move into the toolbar only on desktop (≥821 px, the same
// breakpoint as the desktop shell). Phones keep the Samsung-proved R502/R505 layout where the Tasks lens row
// scrolls with its tool. Falsifier: a phone Tasks view pins the lens row in the toolbar.
export const DESKTOP_WORKSPACE_QUERY = "(min-width: 821px)";
export function useDesktopWorkspace(): boolean {
  const [desktop, setDesktop] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(DESKTOP_WORKSPACE_QUERY).matches);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(DESKTOP_WORKSPACE_QUERY);
    const update = () => setDesktop(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return desktop;
}
